import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Copy, Archive, Send, CheckCircle2, Rocket, Undo2, Timer,
  Dumbbell, AlertTriangle, ChevronUp, ChevronDown, Trash2, X, BarChart3, ClipboardCheck, ListChecks, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import VideoPicker from "../components/VideoPicker";

const GOLD = "#f59e0b";
const STATUS_FR = {
  draft: "Brouillon", research: "Recherche", pending_review: "À valider",
  approved: "Approuvé", published: "Publié", archived: "Archivé",
};
const STATUS_COLORS = {
  draft: "bg-zinc-700 text-zinc-200", research: "bg-blue-900/60 text-blue-200",
  pending_review: "bg-amber-900/60 text-amber-200", approved: "bg-emerald-900/60 text-emerald-200",
  published: "bg-green-600 text-white", archived: "bg-zinc-800 text-zinc-500",
};
const FOCUS_FR = {
  warmup: "Échauffement", mechanics: "Mécanique", shadow: "Shadow", bag_pads: "Sac/Paos",
  combination: "Combinaison", cooldown: "Récupération", validation: "Validation",
};
const EQUIP = ["none", "mirror", "bag", "focus_mitts", "thai_pads", "partner", "timer"];
const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

const EMPTY = {
  title: "", shortDescription: "", memberIntro: "", disciplineId: "kickboxing_muaythai",
  beltLevelId: "orange", targetAudience: "all", contentType: "technique", accessLevel: "members",
  durationMinutes: 20, estimatedIntensity: "light", equipment: ["none", "timer"],
  requiresSupervision: false, contactLevel: "none", safetyNotice: "",
  objectives: [], prerequisites: [], keyPoints: [], commonErrors: [], successCriteria: [],
  studentReflectionQuestions: [], linkedTechniqueIds: [], linkedCombinationIds: [],
  linkedVideoIds: [], linkedCurriculumIds: [], linkedTrainingProgramIds: [],
  sessionBlocks: [], noEquipmentVariant: null, bagVariant: null, padsPartnerVariant: null,
  internalResearchNotes: "", instructorNotes: "",
};

function Badge({ status }) {
  return (
    <span data-testid={`status-badge-${status}`}
      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[status] || "bg-zinc-700"}`}>
      {STATUS_FR[status] || status}
    </span>
  );
}

// ── Éditeur de blocs chronométrés ──────────────────────────────────────────
function BlocksEditor({ blocks, onChange }) {
  const total = blocks.reduce((s, b) => s + (Number(b.durationMinutes) || 0), 0);
  const set = (i, k, v) => { const c = [...blocks]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= blocks.length) return;
    const c = [...blocks]; [c[i], c[j]] = [c[j], c[i]]; c.forEach((b, k) => (b.order = k + 1)); onChange(c);
  };
  const add = () => onChange([...blocks, {
    id: Math.random().toString(36).slice(2, 10), order: blocks.length + 1, title: "", durationMinutes: 0,
    instructions: [], rounds: null, roundDurationSeconds: null, restDurationSeconds: null,
    focus: "mechanics", memberVisible: true,
  }]);
  const rm = (i) => onChange(blocks.filter((_, k) => k !== i));
  return (
    <div className="space-y-3" data-testid="blocks-editor">
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-400">Total blocs : <b style={{ color: GOLD }}>{total} min</b></span>
        <button onClick={add} data-testid="add-block-btn"
          className="inline-flex items-center gap-1 text-sm border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 px-3 py-1.5 rounded-lg">
          <Plus className="w-4 h-4" /> Ajouter un bloc
        </button>
      </div>
      {blocks.map((b, i) => (
        <div key={b.id} className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/50" data-testid={`block-${i}`}>
          <div className="flex gap-2 items-center mb-2">
            <span className="text-xs text-zinc-500 w-6">#{i + 1}</span>
            <input value={b.title} onChange={(e) => set(i, "title", e.target.value)} placeholder="Titre du bloc"
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm" data-testid={`block-title-${i}`} />
            <input type="number" value={b.durationMinutes} onChange={(e) => set(i, "durationMinutes", Number(e.target.value))}
              className="w-16 bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm" title="Minutes" data-testid={`block-mins-${i}`} />
            <span className="text-xs text-zinc-500">min</span>
            <select value={b.focus} onChange={(e) => set(i, "focus", e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm" data-testid={`block-focus-${i}`}>
              {Object.entries(FOCUS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={() => move(i, -1)} className="text-zinc-400 hover:text-white"><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => move(i, 1)} className="text-zinc-400 hover:text-white"><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => rm(i)} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
          </div>
          <textarea value={arrToLines(b.instructions)} onChange={(e) => set(i, "instructions", linesToArr(e.target.value))}
            rows={2} placeholder="Consignes (une par ligne)" className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm mb-2" />
          <div className="flex gap-2 text-xs text-zinc-400">
            <label className="flex items-center gap-1">Rounds <input type="number" value={b.rounds ?? ""} onChange={(e) => set(i, "rounds", e.target.value === "" ? null : Number(e.target.value))} className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1 py-1" /></label>
            <label className="flex items-center gap-1">Durée round (s) <input type="number" value={b.roundDurationSeconds ?? ""} onChange={(e) => set(i, "roundDurationSeconds", e.target.value === "" ? null : Number(e.target.value))} className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1 py-1" /></label>
            <label className="flex items-center gap-1">Repos (s) <input type="number" value={b.restDurationSeconds ?? ""} onChange={(e) => set(i, "restDurationSeconds", e.target.value === "" ? null : Number(e.target.value))} className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1 py-1" /></label>
            <label className="flex items-center gap-1 ml-auto"><input type="checkbox" checked={b.memberVisible} onChange={(e) => set(i, "memberVisible", e.target.checked)} /> Visible membre</label>
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-3">
      <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}
const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100";

// ── Éditeur de séance ───────────────────────────────────────────────────────
function SessionEditor({ session, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...session });
  const [saving, setSaving] = useState(false);
  const isNew = !session?.id;
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const total = (f.sessionBlocks || []).reduce((s, b) => s + (Number(b.durationMinutes) || 0), 0);

  const save = async () => {
    if (!f.title.trim()) { toast.error("Le titre est requis."); return; }
    setSaving(true);
    try {
      const payload = { ...f, durationMinutes: Number(f.durationMinutes) };
      const { data } = isNew
        ? await api.post("/training/admin/sessions", payload)
        : await api.put(`/training/admin/sessions/${session.id}`, payload);
      toast.success(isNew ? "Séance créée (brouillon)." : "Séance enregistrée.");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" data-testid="session-editor">
      <div className="w-full max-w-3xl h-full overflow-y-auto bg-zinc-950 border-l border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-zinc-950 py-2 z-10">
          <h2 className="text-xl font-bold" style={{ color: GOLD }}>{isNew ? "Nouvel entraînement" : "Modifier l'entraînement"}</h2>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid="save-session-btn"
              className="bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-black font-semibold px-4 py-2 rounded-lg text-sm">
              {saving ? "Enregistrement…" : "Enregistrer le brouillon"}
            </button>
            <button onClick={onClose} data-testid="close-editor-btn" className="text-zinc-400 hover:text-white p-2"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <h3 className="text-sm font-bold text-amber-300 mt-4 mb-2">1. Informations générales</h3>
        <Field label="Titre"><input value={f.title} onChange={(e) => up("title", e.target.value)} className={inputCls} data-testid="edit-title" /></Field>
        <Field label="Description courte"><input value={f.shortDescription} onChange={(e) => up("shortDescription", e.target.value)} className={inputCls} data-testid="edit-shortdesc" /></Field>
        <Field label="Intro membre"><input value={f.memberIntro} onChange={(e) => up("memberIntro", e.target.value)} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ceinture"><input value={f.beltLevelId || ""} onChange={(e) => up("beltLevelId", e.target.value)} className={inputCls} /></Field>
          <Field label="Type">
            <select value={f.contentType} onChange={(e) => up("contentType", e.target.value)} className={inputCls}>
              {["technique", "combination", "review", "assessment_prep", "conditioning"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Durée totale (min)">
            <select value={f.durationMinutes} onChange={(e) => up("durationMinutes", Number(e.target.value))} className={inputCls} data-testid="edit-duration">
              {[20, 25, 30].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Accès">
            <select value={f.accessLevel} onChange={(e) => up("accessLevel", e.target.value)} className={inputCls}>
              {["members", "premium", "dojo_only"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Intensité">
            <select value={f.estimatedIntensity} onChange={(e) => up("estimatedIntensity", e.target.value)} className={inputCls}>
              {["light", "moderate", "moderate_high"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Public">
            <select value={f.targetAudience} onChange={(e) => up("targetAudience", e.target.value)} className={inputCls}>
              {["all", "adult", "youth", "kids"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        {total !== Number(f.durationMinutes) && (f.sessionBlocks || []).length > 0 && (
          <p className="text-xs text-rose-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> La somme des blocs ({total}) ≠ durée totale ({f.durationMinutes}). Corrige avant publication.</p>
        )}

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">3. Liens (techniques, vidéos, carnets)</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="IDs techniques liées" hint="Requis pour publier — colle les IDs de techniques existantes"><textarea value={arrToLines(f.linkedTechniqueIds)} onChange={(e) => up("linkedTechniqueIds", linesToArr(e.target.value))} rows={2} className={inputCls} data-testid="edit-linked-tech" /></Field>
          <div className="col-span-2"><Field label="Vidéos démo (bibliothèque vidéo / Bunny)" hint="Visibles par le membre dans le lecteur de la séance"><VideoPicker value={f.linkedVideoIds || []} onChange={(ids) => up("linkedVideoIds", ids)} discipline={f.disciplineId} /></Field></div>
          <Field label="IDs combinaisons"><textarea value={arrToLines(f.linkedCombinationIds)} onChange={(e) => up("linkedCombinationIds", linesToArr(e.target.value))} rows={2} className={inputCls} /></Field>
          <Field label="IDs carnets/curriculum"><textarea value={arrToLines(f.linkedCurriculumIds)} onChange={(e) => up("linkedCurriculumIds", linesToArr(e.target.value))} rows={2} className={inputCls} /></Field>
        </div>

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">4. Objectifs & points techniques</h3>
        <Field label="Objectifs (un par ligne)"><textarea value={arrToLines(f.objectives)} onChange={(e) => up("objectives", linesToArr(e.target.value))} rows={3} className={inputCls} data-testid="edit-objectives" /></Field>
        <Field label="Prérequis"><textarea value={arrToLines(f.prerequisites)} onChange={(e) => up("prerequisites", linesToArr(e.target.value))} rows={2} className={inputCls} /></Field>
        <Field label="Points clés"><textarea value={arrToLines(f.keyPoints)} onChange={(e) => up("keyPoints", linesToArr(e.target.value))} rows={3} className={inputCls} /></Field>

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">5. Erreurs, sécurité & réussite</h3>
        <Field label="Erreurs fréquentes"><textarea value={arrToLines(f.commonErrors)} onChange={(e) => up("commonErrors", linesToArr(e.target.value))} rows={2} className={inputCls} /></Field>
        <Field label="Avertissement de sécurité" hint="Requis pour publier"><textarea value={f.safetyNotice} onChange={(e) => up("safetyNotice", e.target.value)} rows={2} className={inputCls} data-testid="edit-safety" /></Field>
        <Field label="Critères de réussite"><textarea value={arrToLines(f.successCriteria)} onChange={(e) => up("successCriteria", linesToArr(e.target.value))} rows={2} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact">
            <select value={f.contactLevel} onChange={(e) => up("contactLevel", e.target.value)} className={inputCls}>
              {["none", "technical_controlled", "instructor_supervised"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Matériel">
            <div className="flex flex-wrap gap-1.5 pt-1">
              {EQUIP.map((eq) => (
                <button key={eq} type="button" onClick={() => up("equipment", f.equipment.includes(eq) ? f.equipment.filter((x) => x !== eq) : [...f.equipment, eq])}
                  className={`text-[11px] px-2 py-1 rounded-full border ${f.equipment.includes(eq) ? "bg-amber-600 text-black border-amber-600" : "border-zinc-600 text-zinc-400"}`}>{eq}</button>
              ))}
            </div>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 mb-3"><input type="checkbox" checked={f.requiresSupervision} onChange={(e) => up("requiresSupervision", e.target.checked)} /> Supervision requise</label>

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">6. Blocs chronométrés</h3>
        <BlocksEditor blocks={f.sessionBlocks || []} onChange={(b) => up("sessionBlocks", b)} />

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">8. Questions d'auto-évaluation</h3>
        <Field label="Questions (une par ligne)"><textarea value={arrToLines(f.studentReflectionQuestions)} onChange={(e) => up("studentReflectionQuestions", linesToArr(e.target.value))} rows={3} className={inputCls} /></Field>

        <h3 className="text-sm font-bold text-amber-300 mt-6 mb-2">9. Notes internes (jamais visibles aux membres)</h3>
        <Field label="Notes de recherche"><textarea value={f.internalResearchNotes || ""} onChange={(e) => up("internalResearchNotes", e.target.value)} rows={2} className={inputCls} /></Field>
        <Field label="Notes instructeur"><textarea value={f.instructorNotes || ""} onChange={(e) => up("instructorNotes", e.target.value)} rows={2} className={inputCls} /></Field>
      </div>
    </div>
  );
}

// ── Module principal ─────────────────────────────────────────────────────────
export default function TrainingSessionsModule() {
  const nav = useNavigate();
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [qF, setQF] = useState("");
  const [editing, setEditing] = useState(null);
  const [queue, setQueue] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF) params.status = statusF;
      if (qF) params.q = qF;
      const { data } = await api.get("/training/admin/sessions", { params });
      setSessions(data.sessions);
    } catch { toast.error("Chargement impossible."); }
    finally { setLoading(false); }
  }, [statusF, qF]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "review") api.get("/training/admin/review-queue").then((r) => setQueue(r.data.sessions)).catch(() => {});
    if (tab === "analytics") api.get("/training/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, [tab]);

  const act = async (id, action, label) => {
    try { await api.post(`/training/admin/sessions/${id}/${action}`); toast.success(label); load(); if (tab === "review") api.get("/training/admin/review-queue").then((r) => setQueue(r.data.sessions)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible."); }
  };
  const seedOrange = async () => {
    try { const { data } = await api.post("/training/admin/seed-orange"); toast.success(`Pilote Orange : ${data.created.length} créées, ${data.skipped.length} déjà présentes.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Seed impossible."); }
  };
  const seedJaune = async () => {
    try { const { data } = await api.post("/training/admin/seed-jaune"); toast.success(`Pilote Jaune : ${data.created.length} créées, ${data.skipped.length} déjà présentes.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Seed impossible."); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto text-zinc-100" data-testid="training-module">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2"><Dumbbell style={{ color: GOLD }} /> Entraînements associés</h1>
          <p className="text-sm text-zinc-400 mt-1">Séances guidées 20-30 min reliées aux techniques. Workflow Brouillon → À valider → Approuvé → Publié.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seedOrange} data-testid="seed-orange-btn" className="text-sm border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 px-3 py-2 rounded-lg">🥋 Charger pilote Orange</button>
          <button onClick={seedJaune} data-testid="seed-jaune-btn" className="text-sm border border-yellow-500/50 text-yellow-200 hover:bg-yellow-500/10 px-3 py-2 rounded-lg">🟡 Charger pilote Jaune</button>
          <button onClick={() => setEditing({})} data-testid="new-session-btn" className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg text-sm"><Plus className="w-4 h-4" /> Nouvel entraînement</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800 mt-4 mb-4">
        {[["sessions", "Séances", ListChecks], ["review", "À valider", ClipboardCheck], ["analytics", "Analytique", BarChart3]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "sessions" && (
        <>
          <div className="flex gap-2 mb-3 flex-wrap">
            <input value={qF} onChange={(e) => setQF(e.target.value)} placeholder="Rechercher…" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" data-testid="search-input" />
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm" data-testid="status-filter">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {loading ? <p className="text-zinc-500">Chargement…</p> : sessions.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-zinc-800 rounded-xl" data-testid="empty-state">
              <p className="text-zinc-400">Aucun entraînement associé pour le moment.</p>
              <p className="text-zinc-600 text-sm mt-1">Créez une séance guidée et reliez-la à une technique, une vidéo ou un carnet existant.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="sessions-list">
              {sessions.map((s) => (
                <div key={s.id} className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/40 flex items-center gap-4 flex-wrap" data-testid={`session-row-${s.id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2"><span className="font-semibold">{s.title}</span><Badge status={s.status} /></div>
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {s.durationMinutes} min</span>
                      <span>Ceinture : {s.beltLevelId || "—"}</span>
                      <span>Accès : {s.accessLevel}</span>
                      {s.publishBlockers?.length > 0 && <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {s.publishBlockers.length} blocage(s)</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => api.get(`/training/admin/sessions/${s.id}`).then((r) => setEditing(r.data))} data-testid={`edit-${s.id}`} className="p-2 text-zinc-300 hover:text-white border border-zinc-700 rounded-lg" title="Modifier"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => nav(`/app/entrainement/${s.id}`)} data-testid={`preview-${s.id}`} className="p-2 text-amber-300 hover:text-amber-200 border border-zinc-700 rounded-lg" title="Aperçu membre"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => act(s.id, "duplicate", "Dupliquée.")} className="p-2 text-zinc-300 hover:text-white border border-zinc-700 rounded-lg" title="Dupliquer"><Copy className="w-4 h-4" /></button>
                    {["draft", "research"].includes(s.status) && <button onClick={() => act(s.id, "submit", "Envoyée à validation.")} data-testid={`submit-${s.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-2 bg-amber-900/50 text-amber-200 rounded-lg"><Send className="w-3.5 h-3.5" /> Valider</button>}
                    {s.status === "approved" && <button onClick={() => act(s.id, "publish", "Publiée !")} data-testid={`publish-${s.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-2 bg-green-700 text-white rounded-lg"><Rocket className="w-3.5 h-3.5" /> Publier</button>}
                    {s.status === "published" && <button onClick={() => act(s.id, "unpublish", "Dépubliée.")} className="inline-flex items-center gap-1 text-xs px-2.5 py-2 bg-zinc-700 rounded-lg"><Undo2 className="w-3.5 h-3.5" /> Dépublier</button>}
                    {s.status !== "archived" && <button onClick={() => act(s.id, "archive", "Archivée.")} className="p-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-lg" title="Archiver"><Archive className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "review" && (
        <div className="space-y-3" data-testid="review-board">
          {queue.length === 0 ? <p className="text-zinc-500 py-10 text-center">Aucune séance à valider.</p> : queue.map((s) => (
            <div key={s.id} className="border border-amber-700/40 rounded-xl p-4 bg-amber-950/10" data-testid={`review-card-${s.id}`}>
              <div className="flex items-center gap-2 mb-1"><span className="font-bold text-lg">{s.title}</span><Badge status={s.status} /></div>
              <div className="text-xs text-zinc-400 flex gap-3 mb-2"><span>{s.disciplineId} · {s.beltLevelId}</span><span>{s.durationMinutes} min</span><span>Modifié : {(s.lastReviewedAt || "").slice(0, 10)}</span></div>
              {s.objectives?.length > 0 && <p className="text-sm text-zinc-300 mb-1">🎯 {s.objectives.slice(0, 2).join(" · ")}</p>}
              {s.safetyNotice && <p className="text-xs text-amber-300 flex items-start gap-1 mb-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {s.safetyNotice}</p>}
              {s.publishBlockers?.length > 0 && <p className="text-xs text-rose-400 mb-2">⚠️ Blocages publication : {s.publishBlockers.join(" ; ")}</p>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(s.id, "return-draft", "Retournée en brouillon.")} className="text-xs px-3 py-2 border border-zinc-600 rounded-lg text-zinc-300">Retourner en brouillon</button>
                <button onClick={() => act(s.id, "approve", "Approuvée.")} data-testid={`approve-${s.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-2 bg-emerald-700 text-white rounded-lg"><CheckCircle2 className="w-3.5 h-3.5" /> Approuver</button>
                <button onClick={() => act(s.id, "publish", "Publiée !")} className="inline-flex items-center gap-1 text-xs px-3 py-2 bg-green-700 text-white rounded-lg"><Rocket className="w-3.5 h-3.5" /> Publier</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "analytics" && analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-panel">
          {[["Séances publiées", analytics.publishedCount], ["Démarrages (7j)", analytics.starts.d7], ["Démarrages (30j)", analytics.starts.d30], ["Taux de complétion", `${analytics.completionRate}%`], ["Terminées", analytics.completedTotal], ["Besoins d'aide", analytics.needsHelpTotal], ["Démarrages total", analytics.starts.total], ["Démarrages (90j)", analytics.starts.d90]].map(([l, v]) => (
            <div key={l} className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/40">
              <div className="text-2xl font-black" style={{ color: GOLD }}>{v}</div>
              <div className="text-xs text-zinc-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      )}

      {editing && <SessionEditor session={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
